import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../services/api';
import toast from 'react-hot-toast';

const RUPEE_SYMBOL = '\u20B9';
const BILLING_WORKFLOW_STORAGE_PREFIX = 'billing-workflow';

function toNumber(value, fallback = 0) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function formatCurrency(value) {
  return `${RUPEE_SYMBOL}${toNumber(value).toFixed(2)}`;
}

function getStockToneClasses(currentStockQty) {
  if (toNumber(currentStockQty) <= 0) {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }

  return 'border-slate-200 bg-slate-50 text-slate-700';
}

export default function AuditDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [invoice, setInvoice] = useState(null);
  const [rows, setRows] = useState([]);
  const [locked, setLocked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [auditError, setAuditError] = useState('');

  useEffect(() => {
    fetchAuditDetail();
  }, [invoiceId]);

  const fetchAuditDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/inventory/audit/${invoiceId}`);
      setInvoice(res.data?.invoice || null);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setLocked(Boolean(res.data?.locked));
    } catch (err) {
      console.error(err);
      toast.error('Failed to load audit detail');
      setRows([]);
      setLocked(false);
    } finally {
      setLoading(false);
    }
  };

  const groupedRows = useMemo(() => {
    const map = {};

    rows.forEach((row) => {
      const key = `${row.invoiceTreatmentId || row.treatmentId}__${row.treatmentName}`;
      if (!map[key]) {
        map[key] = {
          invoiceTreatmentId: row.invoiceTreatmentId || null,
          treatmentId: row.treatmentId,
          treatmentName: row.treatmentName,
          consumables: [],
        };
      }
      map[key].consumables.push(row);
    });

    return Object.values(map);
  }, [rows]);

  const updateRowQuantity = (auditId, nextQuantityUsed) => {
    let blockedConsumableName = '';

    setRows((currentRows) =>
      currentRows.map((row) => {
        if (Number(row.auditId) !== Number(auditId)) {
          return row;
        }

        const quantityUsed = Math.max(0, toNumber(nextQuantityUsed));
        const availableBalanceQty = toNumber(row.currentStockQty);

        if (quantityUsed > availableBalanceQty) {
          blockedConsumableName = row.consumableName;
          return row;
        }

        return {
          ...row,
          quantityUsed,
          amount: Number((quantityUsed * toNumber(row.ratePerUnit)).toFixed(2)),
        };
      })
    );

    if (blockedConsumableName) {
      toast.error(
        `Invalid quantity. Your clinic does not have that much stock of ${blockedConsumableName}.`
      );
    }
  };

  const updateRowVariant = (auditId, selectedConsumableId) => {
    let blockedConsumableName = '';
    let missingVariantSelection = false;

    setRows((currentRows) =>
      currentRows.map((row) => {
        if (Number(row.auditId) !== Number(auditId)) {
          return row;
        }

        const selectedOption =
          row.variantOptions?.find(
            (option) => Number(option.consumableId) === Number(selectedConsumableId)
          ) || null;

        if (!selectedOption) {
          missingVariantSelection = true;
          return {
            ...row,
            variantWarning: 'Selected variant is unavailable. Please choose another variant.',
          };
        }

        const nextRatePerUnit = toNumber(selectedOption?.ratePerUnit);
        const nextUnit = selectedOption?.unit || row.unit;
        const nextCurrentStockQty = toNumber(selectedOption?.currentStockQty);
        const resolvedConsumableId = selectedOption?.consumableId || row.consumableId;
        let quantityUsed = toNumber(row.quantityUsed);

        if (quantityUsed > nextCurrentStockQty) {
          quantityUsed = nextCurrentStockQty;
          blockedConsumableName = row.consumableName;
        }

        return {
          ...row,
          consumableId: resolvedConsumableId,
          variantChosen: selectedOption.typeVariant,
          unit: nextUnit,
          currentStockQty: nextCurrentStockQty,
          ratePerUnit: nextRatePerUnit,
          amount: Number((quantityUsed * nextRatePerUnit).toFixed(2)),
          quantityUsed,
          variantWarning: '',
        };
      })
    );

    if (missingVariantSelection) {
      toast.error('Selected variant is unavailable. Please choose another variant.');
    }

    if (blockedConsumableName) {
      toast.error(
        `Invalid quantity. Your clinic does not have that much stock of ${blockedConsumableName}.`
      );
    }
  };

  const buildPostBillingPrintDocumentsUrl = () => {
    const params = new URLSearchParams({
      tab: 'invoice',
      invoiceId: String(invoiceId),
      workflow: 'post-billing',
      returnToDashboard: '1',
      autofocus: '1',
    });

    const linkedPrescriptionId = searchParams.get('prescriptionId');
    if (linkedPrescriptionId) {
      params.set('prescriptionId', linkedPrescriptionId);
    }

    return `/print-documents?${params.toString()}`;
  };

  const ensurePrintRecordsReady = async () => {
    const validationRequests = [
      axios.get(`/invoices/${invoiceId}`),
      axios.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
    ];

    const linkedPrescriptionId = searchParams.get('prescriptionId');
    if (linkedPrescriptionId) {
      validationRequests.push(axios.get(`/prescriptions/${linkedPrescriptionId}`));
      validationRequests.push(
        axios.get(`/prescriptions/${linkedPrescriptionId}/pdf`, { responseType: 'blob' })
      );
    }

    await Promise.all(validationRequests);
  };

  const openWhatsAppWebHandoff = (whatsappWebUrl) => {
    const normalizedUrl = String(whatsappWebUrl || '').trim();
    if (!normalizedUrl) {
      return {
        attempted: false,
        opened: false,
        warning: 'Billing saved, but no valid WhatsApp Web handoff URL could be prepared.',
      };
    }

    if (!/^https:\/\/(web\.whatsapp\.com|wa\.me)\//i.test(normalizedUrl)) {
      return {
        attempted: false,
        opened: false,
        warning: 'Billing saved, but the WhatsApp Web handoff URL is invalid.',
      };
    }

    const popup = window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    if (!popup) {
      return {
        attempted: true,
        opened: false,
        warning: 'WhatsApp Web was blocked by the browser, but the billing workflow will continue.',
      };
    }

    try {
      popup.opener = null;
    } catch (_error) {
      // Browser restrictions here do not affect workflow completion.
    }

    return {
      attempted: true,
      opened: true,
      warning: '',
    };
  };

  const persistBillingWorkflowSummary = (summary) => {
    try {
      sessionStorage.setItem(
        `${BILLING_WORKFLOW_STORAGE_PREFIX}:${invoiceId}`,
        JSON.stringify(summary)
      );
    } catch (error) {
      console.warn('Unable to persist billing workflow summary:', error);
    }
  };

  const getFinalizeValidationError = () => {
    if (locked) {
      return 'This inventory audit is already finalized.';
    }

    if (!rows.length) {
      return 'No inventory audit rows are available to finalize.';
    }

    const missingVariantRow = rows.find((row) => !row.consumableId);
    if (missingVariantRow) {
      return `Please select an actual variant for ${missingVariantRow.consumableName || 'each consumable'} before finalizing.`;
    }

    const unavailableVariantRow = rows.find((row) => row.variantWarning);
    if (unavailableVariantRow) {
      return unavailableVariantRow.variantWarning;
    }

    const invalidQuantityRow = rows.find(
      (row) => toNumber(row.quantityUsed) > toNumber(row.currentStockQty)
    );
    if (invalidQuantityRow) {
      return `Invalid quantity. Your clinic does not have that much stock of ${invalidQuantityRow.consumableName || 'this consumable'}.`;
    }

    return '';
  };

  const finalizeAudit = async () => {
    if (saving) {
      return;
    }

    const validationError = getFinalizeValidationError();
    if (validationError) {
      setAuditError(validationError);
      toast.error(validationError);
      return;
    }

    setSaving(true);
    setAuditError('');
    try {
      await axios.post('/inventory/audit/finalize', {
        invoiceId,
        rows: rows.map((row) => ({
          auditId: row.auditId,
          consumableId: row.consumableId,
          variantChosen: row.variantChosen,
          quantityUsed: row.quantityUsed,
        })),
      });

      const workflowSummary = {
        invoiceId: Number(invoiceId),
        invoiceNumber: invoice?.invoiceNumber || '',
        paymentType: invoice?.paymentType || '',
        deliveryState: 'audit-finalized',
        delivered: false,
        providerMode: '',
        deliveryMessage: 'Inventory audit finalized successfully.',
        messageText: '',
        paymentLink: '',
        whatsappWebUrl: '',
        whatsappWebAttempted: false,
        whatsappWebOpened: false,
        whatsappWarning: '',
        prescriptionId: searchParams.get('prescriptionId') || '',
        auditFinalized: true,
        finalizedAt: new Date().toISOString(),
      };

      persistBillingWorkflowSummary(workflowSummary);
      setLocked(true);
      toast.success('Inventory audit finalized successfully.');
      navigate('/dashboard');
    } catch (err) {
      console.error(err);
      const message = err.response?.data?.error || 'Failed to finalize audit';
      setAuditError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading audit detail...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="page-title">Inventory Audit</h1>
          <p className="page-subtitle">
            Review treatment-wise consumables for {invoice?.patient?.name || 'Patient'} Invoice #
            {invoice?.invoiceNumber || invoiceId}.
          </p>
        </div>

        <span
          className={`inline-flex items-center self-start rounded-full border px-3 py-1.5 text-xs font-semibold ${
            locked
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-amber-200 bg-amber-50 text-amber-700'
          }`}
        >
          {locked ? 'Audit Finalized' : 'Pending Doctor Review'}
        </span>
      </div>

      <div className="section-card p-5">
        <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div>
            <span className="font-semibold text-slate-800">Patient:</span>{' '}
            {invoice?.patient?.name || '-'}
          </div>
          <div>
            <span className="font-semibold text-slate-800">Patient ID:</span>{' '}
            {invoice?.patient?.patientId || '-'}
          </div>
          <div>
            <span className="font-semibold text-slate-800">Phone:</span>{' '}
            {invoice?.patient?.phone || '-'}
          </div>
        </div>
      </div>

      {groupedRows.length === 0 ? (
        <div className="section-card p-6 text-slate-500">
          No mapped consumables found for this invoice.
        </div>
      ) : (
        groupedRows.map((group) => (
          <div key={group.treatmentId} className="section-card p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold text-slate-900">{group.treatmentName}</h2>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                {group.consumables.some((item) => item.usageScope === 'visit')
                  ? 'Includes visit-level common consumables'
                  : 'Treatment-specific consumables'}
              </span>
            </div>

            <div className="table-shell">
              <div className="mobile-table-scroll">
                <table className="mobile-card-table table-modern">
                  <thead>
                    <tr>
                      <th>Consumable</th>
                      <th>Type / Variant</th>
                      <th>Unit</th>
                      <th>Balance Qty</th>
                      <th>Quantity Used</th>
                      <th>Rate / Unit</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.consumables.map((item) => (
                      <tr key={item.auditId}>
                        <td data-label="Consumable" className="font-semibold text-slate-900">
                          <div className="flex flex-col gap-1">
                            <span>{item.consumableName}</span>
                            {item.usageScope === 'visit' ? (
                              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                                Once per visit
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td data-label="Type / Variant">
                          <select
                            value={item.consumableId || ''}
                            onChange={(e) => updateRowVariant(item.auditId, e.target.value)}
                            disabled={locked}
                            className="input-modern max-w-[220px] py-2 disabled:cursor-not-allowed disabled:bg-slate-100"
                          >
                            {!item.consumableId ? (
                              <option value="">
                                {item.requestedVariantLabel
                                  ? `Requested: ${item.requestedVariantLabel} - select actual variant`
                                  : 'Select actual variant'}
                              </option>
                            ) : null}
                            {(item.variantOptions || []).map((option) => (
                              <option key={option.consumableId} value={option.consumableId}>
                                {option.displayLabel || option.typeVariant}
                              </option>
                            ))}
                          </select>
                          {item.variantWarning ? (
                            <p className="mt-1 max-w-[220px] text-xs font-medium text-rose-600">
                              {item.variantWarning}
                            </p>
                          ) : null}
                        </td>
                        <td data-label="Unit" className="font-medium text-slate-700">{item.unit}</td>
                        <td data-label="Balance Qty">
                          <span
                            className={`inline-flex min-w-[88px] items-center justify-center rounded-full border px-3 py-1.5 text-sm font-semibold ${getStockToneClasses(
                              item.currentStockQty
                            )}`}
                          >
                            {toNumber(item.currentStockQty).toFixed(2)}
                          </span>
                        </td>
                        <td data-label="Quantity Used">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={toNumber(item.currentStockQty)}
                            value={item.quantityUsed}
                            onChange={(e) => updateRowQuantity(item.auditId, e.target.value)}
                            disabled={locked}
                            className="input-modern max-w-[120px] py-2 disabled:cursor-not-allowed disabled:bg-slate-100"
                          />
                        </td>
                        <td data-label="Rate / Unit" className="font-semibold text-slate-700">
                          {formatCurrency(item.ratePerUnit)}
                        </td>
                        <td data-label="Amount" className="font-semibold text-slate-900">
                          {formatCurrency(item.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))
      )}

      <div className="mobile-sticky-actions mobile-stack-actions flex flex-wrap gap-3 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-[0_-10px_24px_rgba(15,23,42,0.08)] backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <button onClick={() => navigate('/inventory')} className="btn-secondary">
          Back
        </button>

        <button
          onClick={finalizeAudit}
          disabled={saving || rows.length === 0 || locked}
          className="btn-primary"
        >
          {locked ? 'Audit Finalized' : saving ? 'Finalizing...' : 'Finalize Audit'}
        </button>
      </div>

      {auditError ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">
          {auditError}
        </div>
      ) : null}
    </div>
  );
}
